import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { getBriefVersion } from '@disco/api/briefs'
import api from '@disco/api/server'
import {
  getBrief,
  loadBrief,
} from '@disco/state-management/reducers/entities/briefs'
import { Brief } from '@disco/types/types/brief'

import { BusinessData } from 'types/business'
import { Group } from 'types/group'
import { InboxGroup } from 'types/inbox'

import { Spinner } from 'components/ui'

import { useDispatch, useSelector } from 'hooks/use-redux'

import { SubmissionForm } from 'features/briefs/components/submission-form'

export const BriefEditorPreviewPage = () => {
  const params = useParams<{ briefId: string; inboxId: string }>()
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()

  const [businessData, setBusinessData] = useState<BusinessData | null>(null)
  const [inboxData, setInboxData] = useState<Group | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [briefVersion, setBriefVersion] = useState<Brief | null>(null)

  useEffect(() => {
    const loadBriefVersion = async () => {
      try {
        const briefVersion = await getBriefVersion(
          parseInt(searchParams.get('version') || null),
        )
        setBriefVersion(briefVersion)
      } catch (error) {
        setError(error.message)
        setBriefVersion(null)
      }
    }

    if (searchParams.get('version')) {
      loadBriefVersion()
    } else {
      dispatch(loadBrief(parseInt(params.briefId)))
    }

    const fetchBusinessData = async () => {
      try {
        const businessData = await api.getCurrentBusinessPublic()
        setBusinessData(businessData)
      } catch (error) {
        setError(error.message)
      }
    }
    const fetchInboxData = async () => {
      try {
        const group = await api.getGroup(parseInt(params.inboxId!))
        setInboxData(group)
      } catch (error) {
        setError(error.message)
      }
    }

    fetchBusinessData()
    fetchInboxData()
  }, [params.briefId, params.inboxId, dispatch, searchParams])

  const brief = useSelector(state =>
    getBrief(state, {
      briefId: parseInt(params.briefId!),
    }),
  )

  const previewGroup: InboxGroup | null = useMemo(() => {
    if (!brief && !briefVersion) return null
    const briefData = briefVersion || brief
    return {
      id: inboxData?.id,
      name: inboxData?.name,
      image: inboxData?.image,
      brief_data: {
        id: briefData.id,
        name: briefData.name,
        description_html: briefData.description_html,
        deadline: briefData.deadline ?? null,
        deadline_timezone: briefData.deadline_timezone,
        recipient: '',
        sender_email: 'sender@example.com',
        sender_name: 'Sender Name',
        submission_status: 'open',
        content_modified: briefData.content_modified ?? null,
      },
      brief_recipient: {
        email: 'Your Email Address',
        name: 'Your Name',
      },
    }
  }, [brief, briefVersion, inboxData])

  const submissionLocation = {
    pathname: '/brief/123',
    search: '?signature=test-signature',
    state: undefined,
    key: undefined,
    hash: undefined,
  }

  if (error) return <div>{error}</div>
  if (!(brief || briefVersion) || !businessData || !inboxData)
    return <Spinner />

  return (
    <SubmissionForm
      group={previewGroup}
      businessData={businessData}
      location={submissionLocation}
      params={{ groupId: inboxData.id.toString(), groupName: inboxData.name }}
      preview
    />
  )
}
